<template>
  <div class="title-input-container">
    <form class="form" autocomplete="false" v-on:submit.prevent="submit">
      <div class="form-input-group">
        <label class="input">
          <!-- NOTE: Placeholder=" " required for animation -->
          <input
            class="input__field"
            v-model="currentTitle"
            type="text"
            placeholder=" "
          />
          <span class="input__name">Outfit title</span>
          <small
            class="form-text text-muted mb-3 action-text"
            v-bind:class="{ 'show-action-text': showActionText }"
            >{{ actionText }}</small
          >
        </label>
      </div>

      <div class="form-btn-group">
        <b-button
          :variant="buttonVariant"
          :disabled="disableButton"
          id="stitch-btn"
          class="stitch-btn btn-block btn-raised1"
          type="submit"
          >{{ buttonText }}</b-button
        >
        <b-tooltip
          target="stitch-btn"
          placement="top"
          title="Please enter Outfit title!"
          v-if="currentTitle.length === 0"
        ></b-tooltip>

        <small class="form-text pull-left mb-3 ready-to-stitch-text">
          <span v-html="selectedItemText"></span>
        </small>
      </div>
    </form>
  </div>
</template>

<script>
import { bus } from "@/main";
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      showActionText: false,
      actionText: "",
      currentTitle: "",
      outfitSizeLimit: 3,
    };
  },

  props: {
    capsuleId: {
      type: Number,
      required: true,
    },
  },

  mounted() {
    this.currentTitle = this.initialTitle;
  },

  methods: {
    ...mapActions(["createOutfit", "destroyOutfit", "updateOutfit"]),

    submit() {
      this.currentUser !== null
        ? this.handleSubmitAction()
        : bus.$emit("openSignUpModal");
    },

    handleSubmitAction() {
      if (this.isStitched && this.titleHasBeenEdited) {
        this.updateOutfit(this.buildPayload);
        this.setActionText("Outfit renamed");
      } else if (this.isStitched) {
        this.destroyOutfit(this.buildPayload);
        this.setActionText("Outfit unstitched");
      } else {
        this.createOutfit(this.buildPayload);
        this.setActionText("Outfit stitched");
      }
    },

    setActionText(text) {
      this.actionText = text;
      this.showActionText = true;
      setTimeout(() => (this.showActionText = false), 2000);
    },
  },

  computed: {
    ...mapGetters(["currentUser", "selectedOutfit"]),

    selectedLayerProducts() {
      return this.selectedOutfit.layerProducts;
    },

    selectedProductCount() {
      return Object.values(this.selectedLayerProducts).filter(Boolean).length;
    },

    disableButton() {
      return this.selectedProductCount < this.outfitSizeLimit;
    },

    titleHasBeenEdited() {
      return this.initialTitle.localeCompare(this.currentTitle.trim()) !== 0;
    },

    isStitched() {
      return this.selectedOutfit.userTitle !== null;
    },

    buttonVariant() {
      return this.isStitched ? "outline-unstitched" : "unstitched";
    },

    buttonText() {
      return this.isStitched && this.titleHasBeenEdited
        ? `Rename`
        : this.isStitched
        ? `Unstitch`
        : `Stitch`;
    },

    selectedItemText() {
      return this.selectedProductCount < this.outfitSizeLimit
        ? `<b>${this.selectedProductCount}</b> of <b>5</b> selected`
        : `&nbsp;`;
    },

    buildPayload() {
      return {
        capsuleId: this.capsuleId,
        outfitId: this.selectedOutfit.outfitId,
        title: this.currentTitle.trim(),
        productIds: Object.values(this.selectedLayerProducts).filter(Boolean),
      };
    },

    initialTitle() {
      return this.selectedOutfit.userTitle
        ? this.selectedOutfit.userTitle
        : this.selectedOutfit.title
        ? this.selectedOutfit.title
        : "";
    },
  },

  watch: {
    initialTitle: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.currentTitle = this.initialTitle;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_colour-palette.scss";
@import "~@/styles/_breakpoints.scss";

$title-input-container-padding-right: 15px;

.title-input-container {
  width: 100%;
  padding-right: $title-input-container-padding-right;
}

.form {
  display: flex;
  flex-flow: column wrap;
  width: 100%;
  margin-right: 15px;

  @include for-tablet-portrait-up {
    flex-flow: row nowrap;
    width: 80%;
  }

  @include for-tablet-landscape-up {
    width: 80%;
  }

  @include for-desktop-up {
    width: calc(60% + #{$title-input-container-padding-right});
  }

  .form-input-group {
    flex-grow: 1;

    @include for-tablet-portrait-up {
      padding-right: 30px;
    }
  }

  .form-btn-group {
    text-align: center;
  }
}

.action-text {
  color: $emerald !important;
  opacity: 0;
  transition: all 0.3s linear;
}

.show-action-text {
  opacity: 1 !important;
}

.title-input-container {
  .stitch-btn {
    min-width: 140px;
    transition: all 0.3s linear;
    border-radius: 0;
  }
}

// Form Input (label)
// ------------------------------
.input {
  display: block;
  position: relative;

  .input__field {
    display: block;
    width: 100%;
    font-size: 1em;
    color: $grey1;
    border: 1px solid $grey5;
    padding: 7px 15px;
    transition: 0.25s;

    &:read-only {
      cursor: default;
    }

    &:focus,
    &:active {
      outline: 0;
      border-color: $primary-accent;
    }

    &:placeholder-shown + .input__name {
      color: #bbb;
      font-size: 1em;
      transform: translate(0, -33px);
    }
  }

  .input__name {
    transform: translate(0, -65px);
    font-size: 12px;
    font-weight: 400;
    color: $grey3;
    position: absolute;
    transition: 0.25s;
    padding-left: 16px;
  }
}

// Ready to stitch
// ------------------------------
.ready-to-stitch-text {
  color: $alizarin;
  margin-top: 15px;
}
</style>



// WEBPACK FOOTER //
// src/components/showCapsule/TitleInput.vue